import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ArrowRight, MapPin, Calendar, ChevronRight, Phone, Loader2 } from "lucide-react";

/**
 * Landing page SEO auto pour une catégorie de réalisations.
 * URL: /realisations/categorie/:slug
 * - h1, intro_text (SEO), filtre items par catégorie, FAQ, CTA, JSON-LD CollectionPage
 */
export default function RealisationCategoryPage() {
  const { slug } = useParams();
  const { settings: s } = useSettings();
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      api.get(`/realization-categories/by-slug/${slug}`).catch(() => ({ data: null })),
      api.get("/realizations").catch(() => ({ data: [] })),
    ]).then(([catRes, itemsRes]) => {
      if (cancelled) return;
      if (!catRes.data) { setNotFound(true); setLoading(false); return; }
      setCategory(catRes.data);
      // Filtre les items par catégorie : on tente de matcher sur slug OU name (pour rétro-compat)
      const cat = catRes.data;
      const matchKeys = [cat.name, cat.slug, cat.h1].map(x => (x || "").toLowerCase()).filter(Boolean);
      setItems((itemsRes.data || []).filter(it => {
        const itc = (it.category || "").toLowerCase();
        return matchKeys.includes(itc);
      }));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [slug]);

  const baseUrl = useMemo(() => (typeof window !== "undefined" ? window.location.origin : ""), []);
  const canonical = `${baseUrl}/realisations/categorie/${slug}`;
  const meta_title = category?.meta_title || (category ? `${category.name} — Réalisations ${s?.brand?.name || ""}`.trim() : "");
  const meta_description = category?.meta_description || category?.description ||
    `Découvrez nos réalisations ${category?.name || ""} : ${items.length} projets réalisés.`;

  const jsonLd = category ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": category.h1 || category.name,
    "description": meta_description,
    "url": canonical,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": items.length,
      "itemListElement": items.slice(0, 20).map((it, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": it.title,
        "url": `${baseUrl}/realisations/${it.id}`,
      })),
    },
  } : null;

  useDocumentMeta({
    title: meta_title,
    description: meta_description,
    image: category?.cover_image || items[0]?.images?.[0],
    type: "website",
    canonical,
    jsonLd,
  });

  if (notFound) return <Navigate to="/realisations" replace />;
  if (loading || !category) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[color:var(--brand-sky)]" />
      </div>
    );
  }

  return (
    <main className="min-h-screen" data-testid="realisation-category-page">
      {/* Breadcrumb */}
      <nav className="bg-[hsl(var(--muted))]/30 border-b border-[hsl(var(--border))]">
        <div className="container mx-auto px-4 py-3 text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:underline">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/realisations" className="hover:underline">Réalisations</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="font-semibold text-[color:var(--brand-navy)]">{category.name}</span>
        </div>
      </nav>

      {/* Hero — h1 + cover */}
      <section
        className="relative py-16 md:py-24 overflow-hidden"
        style={category.cover_image ? {
          backgroundImage: `linear-gradient(rgba(11,34,66,0.85), rgba(11,34,66,0.75)), url(${category.cover_image})`,
          backgroundSize: "cover", backgroundPosition: "center",
          color: "white",
        } : { background: `linear-gradient(135deg, ${category.color}15, ${category.color}05)` }}
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <Badge className="mb-3 bg-white/20 text-white border-0 text-[10px] uppercase tracking-widest font-bold">
            Catégorie · {items.length} projet{items.length > 1 ? "s" : ""}
          </Badge>
          <h1 className={`font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 ${category.cover_image ? "text-white" : "text-[color:var(--brand-navy)]"}`} data-testid="category-h1">
            {category.h1 || category.name}
          </h1>
          {category.description && (
            <p className={`text-lg md:text-xl ${category.cover_image ? "text-white/90" : "text-[hsl(var(--muted-foreground))]"} max-w-2xl`}>
              {category.description}
            </p>
          )}
        </div>
      </section>

      {/* Bloc SEO intro_text */}
      {category.intro_text && (
        <section className="py-10 md:py-14 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <article
              className="prose prose-slate prose-base md:prose-lg max-w-none prose-headings:font-display prose-headings:text-[color:var(--brand-navy)]"
              dangerouslySetInnerHTML={{ __html: markdownToHtml(category.intro_text) }}
              data-testid="category-intro-text"
            />
          </div>
        </section>
      )}

      {/* Galerie items */}
      <section className="py-10 md:py-14 bg-[hsl(var(--muted))]/30">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[color:var(--brand-navy)] mb-8 text-center">
            Nos {items.length} réalisation{items.length > 1 ? "s" : ""} en {category.name}
          </h2>
          {items.length === 0 ? (
            <Card className="p-10 text-center bg-white">
              <p className="text-[hsl(var(--muted-foreground))]">Aucune réalisation dans cette catégorie pour le moment. Revenez bientôt !</p>
              <Button asChild className="mt-4 bg-[color:var(--brand-navy)]"><Link to="/realisations">Voir toutes les réalisations</Link></Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map(it => (
                <Link key={it.id} to={`/realisations/${it.id}`} className="group" data-testid={`category-realisation-card-${it.id}`}>
                  <Card className="overflow-hidden h-full transition-all hover:shadow-xl hover:-translate-y-1">
                    <div className="aspect-video bg-slate-100 relative overflow-hidden">
                      {it.images?.[0] ? (
                        <img src={it.images[0]} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">Pas d'image</div>
                      )}
                      {it.featured && <Badge className="absolute top-2 right-2 bg-amber-500 text-white border-0">★ Featured</Badge>}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-[color:var(--brand-navy)] group-hover:text-[color:var(--brand-sky)] transition-colors line-clamp-2">{it.title}</h3>
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">
                        {it.zone && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{it.zone}</span>}
                        {it.date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{it.date}</span>}
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      {Array.isArray(category.faq) && category.faq.length > 0 && (
        <section className="py-12 md:py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-[color:var(--brand-navy)] mb-8 text-center">Questions fréquentes</h2>
            <div className="space-y-3">
              {category.faq.map((q, i) => (
                <details key={i} className="group bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden" data-testid={`category-faq-${i}`}>
                  <summary className="cursor-pointer px-5 py-4 font-semibold text-[color:var(--brand-navy)] flex items-center justify-between gap-2">
                    {q.question}
                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-4 text-sm text-slate-700 prose-sm prose-p:my-1" dangerouslySetInnerHTML={{ __html: markdownToHtml(q.answer || "") }} />
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA final */}
      {(category.cta_title || category.cta_link) && (
        <section className="py-12 md:py-16 bg-gradient-to-br from-[color:var(--brand-navy)] to-[color:var(--brand-sky)] text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{category.cta_title || "Un projet similaire ?"}</h2>
            <p className="text-white/90 mb-6 text-lg">Discutons de votre projet — devis gratuit sous 24h.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button asChild size="lg" className="bg-white text-[color:var(--brand-navy)] hover:bg-white/90 font-bold">
                <Link to={category.cta_link || "/devis"}>Demander un devis<ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              {s?.phone && (
                <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <a href={`tel:${s.phone}`}><Phone className="w-4 h-4 mr-2" />{s.phone}</a>
                </Button>
              )}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

// Mini markdown → HTML (sans dépendance) : gras, italique, liens, listes, sauts de ligne
function markdownToHtml(md) {
  if (!md) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = esc(md);
  html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline">$1</a>');
  html = html.replace(/^- (.+)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>.+<\/li>\n?)+/g, m => `<ul>${m}</ul>`);
  html = html.replace(/\n\n/g, "</p><p>");
  return `<p>${html}</p>`;
}
